import { useTranslation } from 'react-i18next';

import createOrder from '@/shared/api/orders/createOrder';
import useModalDialog from '@/shared/hooks/useModalDialog';
import useNotificationBar from '@/shared/hooks/useNotificationBar';
import useProgressDialog from '@/shared/hooks/useProgressDialog';
import useRedirectLogin from '@/shared/hooks/useRedirectLogin';
import DialogName from '@/shared/types/dialogName.enum';
import { OrderCreate } from '@/shared/types/order/order.type';
import OrderCustomer from '@/shared/types/order/orderCustomer.type';
import ProductVariant from '@/shared/types/products/productVariant.type';

type OnUpdateDialogState = ReturnType<
  typeof useModalDialog<unknown, DialogName>
>['onUpdateDialogState'];

type OnUpdateNotificationBar = ReturnType<
  typeof useNotificationBar
>['onUpdateNotificationBar'];

type OnUpdateProgressDialog = ReturnType<
  typeof useProgressDialog
>['onUpdateProgressDialog'];

type Args = {
  isBillingSameAsDelivery: boolean;
  onUpdateDialogState: OnUpdateDialogState;
  onUpdateNotificationBar: OnUpdateNotificationBar;
  onUpdateProgressDialog: OnUpdateProgressDialog;
  setShouldResetForm: (value: boolean) => void;
};

const useOrderCreateOperations = ({
  isBillingSameAsDelivery,
  onUpdateDialogState,
  onUpdateNotificationBar,
  onUpdateProgressDialog,
  setShouldResetForm,
}: Args) => {
  const { t } = useTranslation(['orders', 'customers', 'common']);
  const redirectLogin = useRedirectLogin();

  const handleSaveOrder = async ({
    selectedCustomer,
    selectedProducts,
    currency,
    discount_percent,
  }: {
    selectedCustomer: OrderCustomer;
    selectedProducts: ProductVariant[];
    currency: string;
    discount_percent?: string;
  }) => {
    onUpdateProgressDialog({ open: true, title: t('orders:creatingNewOrder') });
    onUpdateDialogState(DialogName.SaveOrder, { loading: true });

    const getDeliveryDetails = (isBillingSameAsDelivery: boolean) => {
      if (!isBillingSameAsDelivery) {
        return {
          delivery_address: selectedCustomer.delivery_address,
          delivery_address2: selectedCustomer.delivery_address2,
          delivery_city: selectedCustomer.delivery_city,
          delivery_country: selectedCustomer.delivery_country,
          delivery_first_name: selectedCustomer.delivery_first_name,
          delivery_last_name: selectedCustomer.delivery_last_name,
          delivery_phone: selectedCustomer.delivery_phone,
          delivery_postcode: selectedCustomer.delivery_postcode,
          delivery_email_address: selectedCustomer.delivery_email_address,
          delivery_state: selectedCustomer.delivery_state,
        };
      }
    };

    const setPriceValue = (product: ProductVariant) => {
      const priceByCurrency = product?.product_prices.find(
        (item) => item.currency_code === currency,
      );
      return priceByCurrency ? priceByCurrency.value : product.value.toFixed(0);
    };

    const requestBody: OrderCreate = {
      currency,
      discount_percent,
      products: selectedProducts.map((product) => ({
        product_id: product.product_id,
        product_qty: (product.product_qty as number).toFixed(0),
        price: setPriceValue(product),
        variant_id: product.variant_id || undefined,
      })),
      in_customer_id: selectedCustomer.in_customer_id,
      billing_address: selectedCustomer.billing_address,
      billing_address2: selectedCustomer.billing_address2,
      billing_city: selectedCustomer.billing_city,
      billing_country: selectedCustomer.billing_country,
      billing_first_name: selectedCustomer.billing_first_name,
      billing_last_name: selectedCustomer.billing_last_name,
      billing_phone: selectedCustomer.billing_phone,
      billing_postcode: selectedCustomer.billing_postcode,
      billing_state: selectedCustomer.billing_state,
      billing_email_address: selectedCustomer.billing_email_address,
      billing_same_as_delivery: isBillingSameAsDelivery,
      ...getDeliveryDetails(isBillingSameAsDelivery),
    };

    const response = await createOrder({
      body: requestBody,
      onUnauthorized: redirectLogin,
    });

    if (response.success) {
      onUpdateDialogState(DialogName.SaveOrder, {
        open: false,
        loading: false,
        data: response.data[0].in_order_id,
      });
      onUpdateNotificationBar({
        message: t('orders:orderCreated'),
        severity: 'success',
        open: true,
      });
      onUpdateProgressDialog({ open: false });
      setShouldResetForm(true);
    } else {
      onUpdateProgressDialog({ open: false });
      onUpdateDialogState(DialogName.SaveOrder, {
        errors: [response.msg],
        loading: false,
      });
    }
  };

  return {
    handleSaveOrder,
  };
};

export default useOrderCreateOperations;
